import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Mic } from 'lucide-react';

export default function VoicePlayer({ src, isMine, duration: durationProp }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(durationProp || 0);
  const audioRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  const handlePlayPause = (e) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      audio.pause();
      if (animRef.current) cancelAnimationFrame(animRef.current);
      setPlaying(false);
    } else {
      audio.play().then(() => {
        setPlaying(true);
        const update = () => {
          if (audio.duration) {
            setProgress(audio.currentTime / audio.duration);
            setDuration(Math.round(audio.duration));
          }
          if (!audio.paused) {
            animRef.current = requestAnimationFrame(update);
          }
        };
        update();
      }).catch(() => {});
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    setProgress(0);
    if (animRef.current) cancelAnimationFrame(animRef.current);
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current?.duration && isFinite(audioRef.current.duration)) {
      setDuration(Math.round(audioRef.current.duration));
    }
  };

  const handleSeek = (e) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    audio.currentTime = pct * audio.duration;
    setProgress(pct);
  };

  const formatTime = (secs) => {
    if (!secs || !isFinite(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.round(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Generate static waveform bars (decorative)
  const bars = Array.from({ length: 28 }, (_, i) => {
    const seed = (i * 7 + 3) % 10;
    return 0.2 + (seed / 10) * 0.8;
  });

  const currentTime = audioRef.current?.currentTime || 0;

  return (
    <div className="flex items-center gap-2.5 min-w-[200px] max-w-[260px]" data-testid="voice-player">
      <audio
        ref={audioRef}
        src={src}
        onEnded={handleEnded}
        onLoadedMetadata={handleLoadedMetadata}
        preload="metadata"
      />

      {/* Play/Pause circle */}
      <button
        onClick={handlePlayPause}
        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
          isMine
            ? 'bg-white/25 hover:bg-white/35'
            : 'bg-[#FF9933]/15 hover:bg-[#FF9933]/25'
        }`}
        data-testid="voice-play-btn"
      >
        {playing ? (
          <Pause className={`w-5 h-5 ${isMine ? 'text-white' : 'text-[#FF9933]'}`} />
        ) : (
          <Play className={`w-5 h-5 ml-0.5 ${isMine ? 'text-white' : 'text-[#FF9933]'}`} />
        )}
      </button>

      <div className="flex-1 min-w-0">
        {/* Waveform */}
        <div
          className="flex items-center gap-[1.5px] h-6 cursor-pointer"
          onClick={handleSeek}
          data-testid="voice-waveform"
        >
          {bars.map((h, i) => {
            const filledPct = progress * bars.length;
            const isFilled = i < filledPct;
            return (
              <div
                key={i}
                className={`w-[3px] rounded-full transition-colors duration-100 ${
                  isFilled
                    ? isMine ? 'bg-white' : 'bg-[#FF9933]'
                    : isMine ? 'bg-white/30' : 'bg-[#FF9933]/25'
                }`}
                style={{ height: `${Math.max(4, h * 22)}px` }}
              />
            );
          })}
        </div>

        {/* Duration */}
        <div className="flex items-center justify-between mt-0.5">
          <span className={`text-[10px] font-mono ${isMine ? 'text-white/60' : 'text-slate-400'}`}>
            {playing ? formatTime(currentTime) : formatTime(duration)}
          </span>
          <div className="flex items-center gap-0.5">
            <Mic className={`w-3 h-3 ${isMine ? 'text-white/40' : 'text-slate-300'}`} />
          </div>
        </div>
      </div>
    </div>
  );
}
